/**
 * ZIP-Dateien lesen ohne Bibliothek (Excel-Import, Word-Import): Einträge aus dem zentralen Verzeichnis, Entpacken über
 * DecompressionStream (Browser, Node 18+). Unterstützt „gespeichert“ (0) und „Deflate“ (8), wie Office-Dateien sie nutzen.
 */
export interface ZipFile { name: string; method: number; compressedSize: number; offset: number }
export interface ZipMeldungen { invalid: string; damaged: string; unknownMethod: string }

const u16 = (b: Uint8Array, o: number): number => b[o]! | (b[o + 1]! << 8);
const u32 = (b: Uint8Array, o: number): number => (b[o]! | (b[o + 1]! << 8) | (b[o + 2]! << 16) | (b[o + 3]! << 24)) >>> 0;

/** Einträge aus dem zentralen Verzeichnis eines ZIP. */
export function zipEntries(b: Uint8Array, m: ZipMeldungen): ZipFile[] {
  let eocd = -1;
  for (let i = b.length - 22; i >= Math.max(0, b.length - 65557); i--) {
    if (u32(b, i) === 0x06054b50) { eocd = i; break; }
  }
  if (eocd < 0) throw new Error(m.invalid);
  const count = u16(b, eocd + 10);
  let p = u32(b, eocd + 16);
  const out: ZipFile[] = [];
  const dec = new TextDecoder();
  for (let n = 0; n < count; n++) {
    if (u32(b, p) !== 0x02014b50) throw new Error(m.damaged);
    const method = u16(b, p + 10);
    const compressedSize = u32(b, p + 20);
    const nameLen = u16(b, p + 28), extraLen = u16(b, p + 30), commentLen = u16(b, p + 32);
    const offset = u32(b, p + 42);
    out.push({ name: dec.decode(b.subarray(p + 46, p + 46 + nameLen)), method, compressedSize, offset });
    p += 46 + nameLen + extraLen + commentLen;
  }
  return out;
}

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** Inhalt eines Eintrags als Text (UTF-8). */
export async function readZipText(b: Uint8Array, f: ZipFile, m: ZipMeldungen): Promise<string> {
  const nameLen = u16(b, f.offset + 26), extraLen = u16(b, f.offset + 28);
  const start = f.offset + 30 + nameLen + extraLen;
  const raw = b.subarray(start, start + f.compressedSize);
  const data = f.method === 0 ? raw : f.method === 8 ? await inflateRaw(raw) : null;
  if (!data) throw new Error(m.unknownMethod);
  return new TextDecoder().decode(data);
}

const ENTITIES: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'" };
/** XML-Entitäten auflösen (&amp; &#x41; …). */
export function unescapeXml(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos);/gi, (_m, e: string) => {
    if (e[0] === "#") return String.fromCodePoint(e[1] === "x" || e[1] === "X" ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10));
    return ENTITIES[e.toLowerCase()] ?? _m;
  });
}
